import useBucket from './useBucket'
import useBucketMediaMove from './useBucketMediaMove'
import { BucketFragmentFragmentDoc, BucketMediaFragmentFragmentDoc, BucketMediaInTrashDocument, BucketMediaStatus } from '../../../graphql/generated'
import useBucketMedia from './useBucketMedia'

interface DeletePermanentlyParams {
  bucketId: string
  mediaIds: string[]
  onCompleted?: () => void
}

export default function useMediaDeletePermanently() {
  const { mutation } = useBucketMediaMove()
  const { isFreemiumBucket } = useBucket()
  const { refetch: refetchMedia } = useBucketMedia()

  const deletePermanently = async ({ bucketId, mediaIds, onCompleted }: DeletePermanentlyParams) => {
    return mutation({
      variables: { bucketId, mediaIds, data: { deleteMedia: true } },
      update: async (cache, { data }) => {
        if (!data?.bucketMediaMove) return
        data.bucketMediaMove?.forEach((id) => {
          // Retrieve current media
          const media = cache.readFragment({ id: `BucketMedia:${id}`, fragment: BucketMediaFragmentFragmentDoc })

          // Modify bucket media count
          cache.updateFragment(
            {
              id: `Bucket:${bucketId}`,
              fragment: BucketFragmentFragmentDoc
            },
            (current) => {
              if (!current) return null

              const totalMediaInTrash = current?.counters?.totalMediaInTrash ?? 0
              const fileSize = media?.fileSize ?? 0
              const totalSizeInTrash = current?.counters?.totalSizeInTrash ?? 0
              const totalMedia = current?.counters?.totalMedia ?? 0
              const totalSize = current?.counters?.totalSize ?? 0

              return {
                ...current,
                counters: {
                  ...current.counters,
                  totalMediaInTrash: Math.max(totalMediaInTrash - 1, 0),
                  totalSizeInTrash: Math.max(totalSizeInTrash - fileSize, 0),
                  totalMedia: Math.max(totalMedia - 1, 0),
                  totalSize: Math.max(totalSize - fileSize, 0)
                }
              }
            }
          )

          // Remove media from album
          cache.updateQuery(
            {
              query: BucketMediaInTrashDocument,
              variables: {
                bucketId,
                filterBy: { status: BucketMediaStatus.Trash },
                sortBy: null
              }
            },
            (cachedData) => {
              if (!media || !cachedData) return null

              const { bucketMediaByAlbumId } = cachedData
              const currentItems = bucketMediaByAlbumId?.items || []
              const items = currentItems.filter((item) => item.id !== media.id)

              return {
                bucketMediaByAlbumId: {
                  ...bucketMediaByAlbumId,
                  items
                }
              }
            }
          )
        })

        if (isFreemiumBucket) {
          await refetchMedia()
        }
        cache.gc()
      },
      onCompleted: () => {
        onCompleted?.()
      }
    })
  }
  return { deletePermanently }
}
